import { useCallback } from "react";
import type { UseFormSetValue } from "react-hook-form";
import type { CampaignParamForm, ExperimentFormValues, PredictionFlightParamForm } from "./ExperimentForm";

type CampaignParamField = "paramName" | "paramValue";

function updateCampaignParamField(
  params: CampaignParamForm[],
  paramIndex: number,
  field: CampaignParamField,
  value: string,
): CampaignParamForm[] {
  const updatedParams = [...params];
  updatedParams[paramIndex] = { ...updatedParams[paramIndex], [field]: value };
  return updatedParams;
}

function updatePredictionFlightParamField(
  params: PredictionFlightParamForm[],
  paramIndex: number,
  field: "predictorTypeId" | "paramValue",
  value: number | null | string,
): PredictionFlightParamForm[] {
  const updatedParams = [...params];
  updatedParams[paramIndex] = { ...updatedParams[paramIndex], [field]: value };
  return updatedParams;
}

type UseExperimentVariantParamsArgs = {
  campaigns: ExperimentFormValues["campaigns"];
  predictionFlights: ExperimentFormValues["predictionFlights"];
  setValue: UseFormSetValue<ExperimentFormValues>;
};

export function useExperimentVariantParams({ campaigns, predictionFlights, setValue }: UseExperimentVariantParamsArgs) {
  const setCampaignParams = useCallback(
    (campaignIndex: number, params: CampaignParamForm[]) => {
      setValue(`campaigns.${campaignIndex}.params`, params, { shouldDirty: true });
    },
    [setValue],
  );

  const setPredictionFlightParams = useCallback(
    (predictionFlightIndex: number, params: PredictionFlightParamForm[]) => {
      setValue(`predictionFlights.${predictionFlightIndex}.params`, params, { shouldDirty: true });
    },
    [setValue],
  );

  const addCampaignParam = useCallback(
    (campaignIndex: number) => {
      const currentParams = [...(campaigns[campaignIndex]?.params || [])];
      setCampaignParams(campaignIndex, [...currentParams, { paramName: "", paramValue: "" }]);
    },
    [campaigns, setCampaignParams],
  );

  const removeCampaignParam = useCallback(
    (campaignIndex: number, paramIndex: number) => {
      const currentParams = [...(campaigns[campaignIndex]?.params || [])];
      setCampaignParams(
        campaignIndex,
        currentParams.filter((_, index) => index !== paramIndex),
      );
    },
    [campaigns, setCampaignParams],
  );

  const updateCampaignParam = useCallback(
    (campaignIndex: number, paramIndex: number, field: CampaignParamField, value: string) => {
      const currentParams = [...(campaigns[campaignIndex]?.params || [])];
      setCampaignParams(campaignIndex, updateCampaignParamField(currentParams, paramIndex, field, value));
    },
    [campaigns, setCampaignParams],
  );

  const addPredictionFlightParam = useCallback(
    (predictionFlightIndex: number) => {
      const currentParams = [...(predictionFlights[predictionFlightIndex]?.params || [])];
      setPredictionFlightParams(predictionFlightIndex, [...currentParams, { predictorTypeId: null, paramValue: "" }]);
    },
    [predictionFlights, setPredictionFlightParams],
  );

  const removePredictionFlightParam = useCallback(
    (predictionFlightIndex: number, paramIndex: number) => {
      const currentParams = [...(predictionFlights[predictionFlightIndex]?.params || [])];
      setPredictionFlightParams(
        predictionFlightIndex,
        currentParams.filter((_, index) => index !== paramIndex),
      );
    },
    [predictionFlights, setPredictionFlightParams],
  );

  const updatePredictionFlightParamValue = useCallback(
    (predictionFlightIndex: number, paramIndex: number, value: string) => {
      const currentParams = [...(predictionFlights[predictionFlightIndex]?.params || [])];
      setPredictionFlightParams(
        predictionFlightIndex,
        updatePredictionFlightParamField(currentParams, paramIndex, "paramValue", value),
      );
    },
    [predictionFlights, setPredictionFlightParams],
  );

  const updatePredictionFlightParamType = useCallback(
    (predictionFlightIndex: number, paramIndex: number, predictorTypeId: number | null) => {
      const currentParams = [...(predictionFlights[predictionFlightIndex]?.params || [])];
      setPredictionFlightParams(
        predictionFlightIndex,
        updatePredictionFlightParamField(currentParams, paramIndex, "predictorTypeId", predictorTypeId),
      );
    },
    [predictionFlights, setPredictionFlightParams],
  );

  return {
    addCampaignParam,
    removeCampaignParam,
    updateCampaignParam,
    addPredictionFlightParam,
    removePredictionFlightParam,
    updatePredictionFlightParamValue,
    updatePredictionFlightParamType,
  };
}

export type ExperimentVariantParamHandlers = ReturnType<typeof useExperimentVariantParams>;
